import React from 'react'
import PropTypes from 'prop-types'
import { classNames } from 'utils/utils'
import {
  CAN_INPUT_ALLOWABLE_NUM_BITS,
  ALLOWED_NUM_BITS_INPUT_TO_16_BIT_CAN_OUTPUT,
  ALLOWED_NUM_BITS_INPUT_TO_NATIVE_OUTPUT,
  CAN_INPUT_ALLOWABLE_NUM_BITS_NATIVE_OUTPUT,
  CAN_INPUT,
} from 'utils/constants'
import { DEFAULT_NUM_BITS } from 'constants/conditions'
import strings from 'utils/strings'
import { RadioButtonGroup } from 'components/patterns'
import { Row, Col } from 'components/patterns/Layout'
import {
  InputOperatorEditor,
  InputValueEditor,
  HysteresisEditor,
  EdgeEditor,
  BitGrid,
  CanIdEditor,
  InputCustomDescription,
} from 'components/feature/NavigatorPanes/editors/base'
import CanOutputSection from '../../OutputNodePane/OutputNodeEditor/CanOutputSection'
import { OutputSectionContainer } from './CanInputEditor.style'

const bitsButtons = (
  hasSixteenBitOutput,
  hasNativeOutput,
  isOutputBoolean,
  isLogicSet
) => {
  const getDisabled = (num) => {
    if (!isOutputBoolean && hasNativeOutput)
      return !CAN_INPUT_ALLOWABLE_NUM_BITS_NATIVE_OUTPUT.includes(num)
    if (hasSixteenBitOutput)
      return !ALLOWED_NUM_BITS_INPUT_TO_16_BIT_CAN_OUTPUT.includes(num)
    if (hasNativeOutput && !isLogicSet)
      return !ALLOWED_NUM_BITS_INPUT_TO_NATIVE_OUTPUT.includes(num)
    return false
  }

  return CAN_INPUT_ALLOWABLE_NUM_BITS.map((num) => ({
    key: num,
    value: num,
    title: num.toString(),
    disabled: getDisabled(num),
  }))
}

const CanInputEditor = ({
  className,
  isLogicSet,
  numberOfBits,
  startBit,
  onNumberOfBitsChange,
  onStartBitChange,
  canId,
  uri,
  parentQueryUri,
  rungName,
  hasSixteenBitOutput,
  hasNativeOutput,
  isOutputBoolean,
  hasNonBooleanNativeOutput,
  disableFalseValue,
  fnId,
  customDescription,
  inputUri,
  selectedInputDescription,
}) => (
  <div className={classNames(className, 'can-input-editor')}>
    <Row className="editor-row">
      <Col className="editor-column" span={7}>
        <CanIdEditor canId={canId} uri={uri} />
      </Col>
      <Col span={17}>
        <OutputSectionContainer>
          <CanOutputSection
            mappedOutputId={canId}
            numberOfBits={numberOfBits}
            showCanId={false}
            className="output-container"
          />
        </OutputSectionContainer>
      </Col>
    </Row>
    <Row className="editor-row">
      <Col className="editor-column" span={24}>
        <RadioButtonGroup
          label={strings.number_of_bits}
          currentValue={numberOfBits}
          buttons={bitsButtons(
            hasSixteenBitOutput,
            hasNativeOutput,
            isOutputBoolean,
            isLogicSet
          )}
          onChange={onNumberOfBitsChange}
          className="input-type-selector"
          radioGroupClassName="number-of-bits-radio-group"
          vertical
        />
      </Col>
    </Row>
    <BitGrid
      groupSize={numberOfBits}
      type="input"
      disabledBits={[]}
      value={startBit}
      selectionHandler={onStartBitChange}
    />
    <InputCustomDescription
      uri={uri}
      inputUri={inputUri || canId}
      customDescription={customDescription}
      subType={CAN_INPUT}
      placeholder={selectedInputDescription}
    />
    {!hasSixteenBitOutput && (
      <Row
        className="editor-row"
        type="inline"
        justify="space-between"
        align="top"
        style={{ marginLeft: -6, marginRight: -6 }}
      >
        <Col span={14}>
          <InputOperatorEditor uri={uri} fnId={fnId} />
        </Col>
        <Col span={10}>
          <InputValueEditor
            uri={uri}
            fnId={fnId}
            disableFalseValue={disableFalseValue}
            disabled={hasNonBooleanNativeOutput}
          />
        </Col>
      </Row>
    )}

    {isLogicSet && (
      <>
        <HysteresisEditor uri={uri} />

        <EdgeEditor uri={uri} />
      </>
    )}
  </div>
)

CanInputEditor.defaultProps = {
  numberOfBits: DEFAULT_NUM_BITS.SUB_CONDITION,
}

CanInputEditor.propTypes = {
  /** className */
  className: PropTypes.string,
  /** operator determining how the input value is compared against some constant. String of a number (e.g. "2" which means equals ) */
  isLogicSet: PropTypes.bool,
  /** number of bits the CAN input is configured to read */
  numberOfBits: PropTypes.number,
  /** position if the intial bit to be read */
  startBit: PropTypes.number,
  /** handler for updatign the numberOfBits */
  onNumberOfBitsChange: PropTypes.func,
  /** handler for updatign the tartBit */
  onStartBitChange: PropTypes.func,
  /** array if can-ids  available on the feature  */
  featureCanIds: PropTypes.arrayOf(PropTypes.string),
  /** current can id for the given query context */
  canId: PropTypes.string,
  /** uri of the query that hollds the CAN-input info. */
  uri: PropTypes.string,
  /** The uri of the containing logic set or timer */
  parentQueryUri: PropTypes.string,
  /** name of the run in the logic set or timer that this Can ID is part of */
  rungName: PropTypes.string,
  hasSixteenBitOutput: PropTypes.bool,
  hasNativeOutput: PropTypes.bool,
  isOutputBoolean: PropTypes.bool,
  hasNonBooleanNativeOutput: PropTypes.bool,
  disableFalseValue: PropTypes.bool,
  fnId: PropTypes.string,
  customDescription: PropTypes.string,
  inputUri: PropTypes.string,
  selectedInputDescription: PropTypes.string,
}

export default CanInputEditor
